import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Edit2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const KIND_META = {
  "dispositions": {
    title: "Dispositions",
    subtitle: "Outcomes set by agents at the end of a contact (color-coded).",
    extra: ["is_positive", "is_callback", "sort_order"],
  },
  "pause-codes": {
    title: "Pause Codes",
    subtitle: "Break reasons agents may select (Lunch, Tea break, etc.). Set max-duration to enforce limits.",
    extra: ["max_seconds", "is_paid", "sort_order"],
  },
  "wrapup-codes": {
    title: "Wrap-up Codes",
    subtitle: "Post-contact wrap-up reasons. Mark as required to enforce on every contact.",
    extra: ["required", "sort_order"],
  },
};

export default function CatalogsPage({ kind: kindProp }) {
  const params = useParams();
  const kind = kindProp || params.kind;
  const meta = KIND_META[kind] || { title: kind, subtitle: "", extra: [] };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try { const r = await api.get(`/admin/catalogs/${kind}`); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  function startNew() { setForm({ name: "", description: "", color: "#0A195C", is_active: true, sort_order: rows.length + 1, is_positive: false, required: false, is_paid: true, max_seconds: null }); setEditing("new"); }
  function startEdit(r) { setForm({ ...r }); setEditing(r); }
  async function save() {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.max_seconds === "" || payload.max_seconds == null) payload.max_seconds = null;
      else payload.max_seconds = Number(payload.max_seconds);
      if (payload.sort_order != null && payload.sort_order !== "") payload.sort_order = Number(payload.sort_order);
      if (editing === "new") await api.post(`/admin/catalogs/${kind}`, payload);
      else await api.patch(`/admin/catalogs/${kind}/${editing.id}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/admin/catalogs/${kind}/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }
  async function toggleActive(r) {
    try { await api.patch(`/admin/catalogs/${kind}/${r.id}`, { is_active: !r.is_active }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  const sorted = useMemo(() => [...rows].sort((a,b)=> (a.sort_order||0)-(b.sort_order||0)), [rows]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid={`catalog-${kind}-title`}>{meta.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{meta.subtitle}</p>
        </div>
        {canWrite && <Button data-testid={`add-${kind}-btn`} onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
          <Plus className="w-4 h-4" /> Add
        </Button>}
      </div>
      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2 w-8"></th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Description</th>
              {meta.extra.includes("is_positive") && <th className="px-4 py-2">Positive</th>}
              {meta.extra.includes("is_callback") && <th className="px-4 py-2">Callback</th>}
              {meta.extra.includes("required") && <th className="px-4 py-2">Required</th>}
              {meta.extra.includes("is_paid") && <th className="px-4 py-2">Paid</th>}
              {meta.extra.includes("max_seconds") && <th className="px-4 py-2">Max sec</th>}
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2 w-20">Actions</th>
            </tr></thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-cat-${r.id}`}>
                  <td className="px-4 py-2.5"><span className="inline-block w-3 h-3 rounded-full" style={{ background: r.color }} /></td>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.description || "—"}</td>
                  {meta.extra.includes("is_positive") && <td className="px-4 py-2.5">{r.is_positive ? "Yes" : "—"}</td>}
                  {meta.extra.includes("is_callback") && <td className="px-4 py-2.5">{r.is_callback ? "Yes" : "—"}</td>}
                  {meta.extra.includes("required") && <td className="px-4 py-2.5">{r.required ? "Yes" : "—"}</td>}
                  {meta.extra.includes("is_paid") && <td className="px-4 py-2.5">{r.is_paid ? "Paid" : "Unpaid"}</td>}
                  {meta.extra.includes("max_seconds") && <td className="px-4 py-2.5 text-xs">{r.max_seconds ? `${r.max_seconds}s` : "∞"}</td>}
                  <td className="px-4 py-2.5"><Switch data-testid={`active-${r.id}`} checked={!!r.is_active} onCheckedChange={()=>toggleActive(r)} /></td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">
                    {canWrite && <Button data-testid={`edit-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={()=>startEdit(r)}><Edit2 className="w-4 h-4"/></Button>}
                    {canDelete && <Button data-testid={`delete-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={()=>remove(r)}><Trash2 className="w-4 h-4"/></Button>}
                  </div></td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-sm text-muted-foreground">No entries.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing==="new"?`New ${meta.title.replace(/s$/,"")}`:`Edit ${editing?.name}`}</DialogTitle><DialogDescription>{meta.subtitle}</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            <div><Label>Name</Label><Input data-testid="cat-name" value={form.name||""} onChange={(e)=>setForm({...form, name:e.target.value})} /></div>
            <div><Label>Color</Label><Input data-testid="cat-color" value={form.color||""} onChange={(e)=>setForm({...form, color:e.target.value})} /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Input data-testid="cat-desc" value={form.description||""} onChange={(e)=>setForm({...form, description:e.target.value})} /></div>
            <div><Label>Sort order</Label><Input data-testid="cat-sort" type="number" value={form.sort_order??""} onChange={(e)=>setForm({...form, sort_order:e.target.value})} /></div>
            {meta.extra.includes("max_seconds") && <div><Label>Max seconds (blank = unlimited)</Label><Input data-testid="cat-max" type="number" value={form.max_seconds??""} onChange={(e)=>setForm({...form, max_seconds:e.target.value})} /></div>}
            {meta.extra.includes("is_positive") && <div className="flex items-center gap-2 sm:col-span-2"><Switch data-testid="cat-pos" checked={!!form.is_positive} onCheckedChange={(v)=>setForm({...form, is_positive:!!v})} /><Label>Positive outcome</Label></div>}
            {meta.extra.includes("is_callback") && <div className="flex items-center gap-2 sm:col-span-2"><Switch data-testid="cat-callback" checked={!!form.is_callback} onCheckedChange={(v)=>setForm({...form, is_callback:!!v})} /><Label>Callback (agent picks a date/time on wrap-up)</Label></div>}
            {meta.extra.includes("required") && <div className="flex items-center gap-2 sm:col-span-2"><Switch data-testid="cat-req" checked={!!form.required} onCheckedChange={(v)=>setForm({...form, required:!!v})} /><Label>Required</Label></div>}
            {meta.extra.includes("is_paid") && <div className="flex items-center gap-2 sm:col-span-2"><Switch data-testid="cat-paid" checked={!!form.is_paid} onCheckedChange={(v)=>setForm({...form, is_paid:!!v})} /><Label>Paid pause</Label></div>}
            <div className="flex items-center gap-2 sm:col-span-2"><Switch data-testid="cat-active" checked={!!form.is_active} onCheckedChange={(v)=>setForm({...form, is_active:!!v})} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="cat-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
